import { MUSTERILER } from '../constants/filters';

// Müşteri ID → isim eşlemesi
const MUSTERI_MAP = Object.fromEntries(MUSTERILER.map(m => [m.value, m.label]));

const MODUL_RENK = {
  UserAPI:          { bg: 'bg-purple-100', text: 'text-purple-700',  dot: 'bg-purple-400' },
  SurveyAPI:        { bg: 'bg-blue-100',   text: 'text-blue-700',    dot: 'bg-blue-400'   },
  ImportAPI:        { bg: 'bg-teal-100',   text: 'text-teal-700',    dot: 'bg-teal-400'   },
  IntegrationAPI:   { bg: 'bg-indigo-100', text: 'text-indigo-700',  dot: 'bg-indigo-400' },
  ScheduledJobsAPI: { bg: 'bg-orange-100', text: 'text-orange-700',  dot: 'bg-orange-400' },
};

function ModulBadge({ value }) {
  if (!value || value === '-')
    return <span className='text-slate-400 text-xs'>-</span>;
  const r = MODUL_RENK[value] ?? { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5
                      rounded-full text-xs font-medium ${r.bg} ${r.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${r.dot}`} />
      {value}
    </span>
  );
}

function DurumBadge({ renk }) {
  const cfg = {
    green:  { cls: 'bg-green-100 text-green-700',  icon: '✅', label: 'Başarılı' },
    yellow: { cls: 'bg-amber-100 text-amber-700',  icon: '⚠️', label: 'Validasyon' },
    red:    { cls: 'bg-red-100 text-red-700',       icon: '❌', label: 'Hata' },
  }[renk] ?? { cls: 'bg-gray-100 text-gray-600', icon: '•', label: '-' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                      text-xs font-medium ${cfg.cls}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function formatDatetime(val) {
  if (!val || val === '-') return '-';
  try {
    if (typeof val === 'string' && val.includes('T'))
      return new Date(val).toLocaleString('tr-TR');
    const num = parseFloat(val);
    const ms  = num > 9999999999 ? num : num * 1000;
    return new Date(ms).toLocaleString('tr-TR');
  } catch { return val; }
}

function musteriAdi(val) {
  if (!val || val === '-') return '-';
  return MUSTERI_MAP[val] ?? val;
}

/**
 * Gruptan toplam süreyi hesapla (process loglarındaki en büyük süre)
 */
function toplamSure(group) {
  const sureler = group.process
    .map(p => parseInt(p.sureMs))
    .filter(n => !isNaN(n));
  if (sureler.length === 0) return null;
  return Math.max(...sureler);
}

export default function LogTable({ data, total, onRowClick }) {
  if (!data || data.length === 0)
    return (
      <div className='flex flex-col items-center justify-center py-20
                      bg-white rounded-xl border border-gray-200'>
        <div className='w-12 h-12 bg-slate-100 rounded-full flex items-center
                        justify-center mb-3'>
          <span className='text-2xl'>🔍</span>
        </div>
        <p className='text-slate-500 font-medium'>Kayıt bulunamadı</p>
        <p className='text-slate-400 text-sm mt-1'>
          Filtre kriterlerini değiştirmeyi deneyin
        </p>
      </div>
    );

  return (
    <div className='bg-white rounded-xl border border-gray-200 shadow-sm
                    overflow-hidden'>
      <table className='w-full text-sm'>
        <thead>
          <tr className='bg-slate-800 text-white'>
            <th className='text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider'>
              Zaman
            </th>
            <th className='text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider'>
              Müşteri
            </th>
            <th className='text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider'>
              Modül
            </th>
            <th className='text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider'>
              Endpoint
            </th>
            <th className='text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider'>
              Durum
            </th>
            <th className='text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider'>
              Süre (ms)
            </th>
            <th className='text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider'>
              Detay
            </th>
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-100'>
          {data.map((group, i) => {
            const req = group.request ?? {};
            const sure = toplamSure(group);

            // Satır renklendirmesi
            const rowBg = group.renk === 'red'
              ? 'bg-red-50 hover:bg-red-100'
              : group.renk === 'yellow'
              ? 'bg-amber-50 hover:bg-amber-100'
              : i % 2 === 0
              ? 'bg-white hover:bg-green-50'
              : 'bg-green-50/30 hover:bg-green-50';

            // Sol kenar rengi (ilk td'ye uygulanacak)
            const barColor = group.renk === 'red'
              ? 'bg-red-400'
              : group.renk === 'yellow'
              ? 'bg-amber-400'
              : 'bg-green-400';

            // Kısa detay özeti
            let detayOzet = '-';
            if (group.error.length > 0) {
              detayOzet = group.error[0].hataAciklamasi ?? group.error[0].detay ?? '-';
            } else if (group.validation.length > 0) {
              detayOzet = group.validation[0].hatalar ?? group.validation[0].sonuc ?? '-';
            } else if (group.process.length > 0) {
              const anlamli = group.process.find(p => p.detay && p.detay !== '-');
              if (anlamli) detayOzet = anlamli.detay;
            }

            return (
              <tr key={group.requestId}
                onClick={() => onRowClick?.(group)}
                className={`${rowBg} transition-colors
                            ${onRowClick ? 'cursor-pointer' : ''}`}>
                <td className='py-3 pr-4 pl-0 relative'>
                  <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-r ${barColor}`} />
                  <span className='text-xs text-slate-400 whitespace-nowrap pl-3'>
                    {formatDatetime(req.zaman)}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <span className='font-semibold text-slate-800 text-sm'>
                    {musteriAdi(req.musteri)}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <ModulBadge value={req.modul} />
                </td>
                <td className='px-4 py-3'>
                  <code className='text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5
                                   rounded font-mono'>
                    {req.endpoint ?? '-'}
                  </code>
                </td>
                <td className='px-4 py-3'>
                  <DurumBadge renk={group.renk} />
                </td>
                <td className='px-4 py-3'>
                  {sure !== null ? (
                    <span className={`text-xs ${
                      sure > 3000 ? 'text-red-600 font-bold bg-red-50 px-2 py-0.5 rounded'
                      : sure > 1000 ? 'text-amber-600 font-medium'
                      : 'text-slate-500'
                    }`}>
                      {sure.toLocaleString('tr-TR')} ms
                    </span>
                  ) : (
                    <span className='text-slate-400'>-</span>
                  )}
                </td>
                <td className='px-4 py-3'>
                  <span className='text-xs text-slate-600 max-w-[200px] block truncate'
                        title={String(detayOzet)}>
                    {detayOzet}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
